// Small API helper using fetch. Adds JSON handling and Authorization header when token exists.
// Prefer token from Vuex `auth` store, fall back to localStorage for backward compatibility.
import store from "@/store";
const apiBase = ""; // default base (same origin). Can be set via env variables if needed.

let DEBUG = 0;
// DEBUG levels: 0=off, 1=basic, 2=verbose
try {
  DEBUG = Number(localStorage.getItem("debug_api") || "0") || 0;
} catch (e) {
  DEBUG = 0;
}

function getToken() {
  try {
    const t = store && store.getters && store.getters["auth/token"];
    if (t) return t;
  } catch (e) {
    // ignore
  }
  try {
    return localStorage.getItem("token") || null;
  } catch (e) {
    return null;
  }
}

// Simple in-memory cache for GET requests
const getCache = new Map(); // key -> { value, expires }
const DEFAULT_GET_TTL = 30 * 1000; // 30s

// Track active AbortControllers so we can cancel requests on navigation
const activeControllers = new Set();
// Map tags -> Set(AbortController) for per-tag cancellation
const tagMap = new Map();

export function abortAll() {
  for (const ctrl of Array.from(activeControllers)) {
    try {
      ctrl.abort();
    } catch (e) {
      /* ignore */
    }
  }
  activeControllers.clear();
  tagMap.clear();
}

export function abortTag(tag) {
  if (!tag) return;
  const set = tagMap.get(tag);
  if (set) {
    for (const ctrl of Array.from(set)) {
      try {
        ctrl.abort();
      } catch (e) {
        /* ignore */
      }
    }
    set.clear();
    tagMap.delete(tag);
  }
}

function toURL(path) {
  return apiBase + path;
}

function appendQuery(path, params = {}) {
  const raw = path || "";
  const [base, existing = ""] = raw.split("?");
  const search = new URLSearchParams(existing);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry === undefined || entry === null) return;
        search.append(key, String(entry));
      });
      return;
    }
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `${base}?${query}` : base;
}

function normalizeIds(payload) {
  if (!payload) return [];
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload.results)
    ? payload.results
    : [];

  return source
    .map((item) => {
      if (typeof item === "number") return item;
      if (
        item &&
        typeof item === "object" &&
        item.id !== undefined &&
        item.id !== null
      )
        return Number(item.id);
      return Number.NaN;
    })
    .filter((id) => Number.isFinite(id));
}

async function request(path, options = {}) {
  const url = toURL(path);

  // Default headers
  const headers = new Headers(options.headers || {});
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (!(options.body instanceof FormData) && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");

  const token = getToken();
  let tokenSource = null;
  if (token) {
    // The backend expects the raw token in the Authorization header (no scheme)
    headers.set("Authorization", token);
    tokenSource = "store|localStorage";
  }

  // Build final opts
  const opts = Object.assign({}, options, { headers });
  const nonAbortable = Boolean(opts.nonAbortable || opts.persistent);

  // Support AbortController: use provided signal or create one and register it
  let controllerUsed = null;
  const requestTag = opts.tag || opts.requestTag || null;
  if (!opts.signal && !nonAbortable) {
    const c = new AbortController();
    opts.signal = c.signal;
    controllerUsed = c;
    activeControllers.add(c);
    // register by tag if provided
    if (requestTag) {
      let set = tagMap.get(requestTag);
      if (!set) {
        set = new Set();
        tagMap.set(requestTag, set);
      }
      set.add(c);
    }
  }

  // Simple GET caching (only for GET and when cacheTTL provided or default)
  const isGet = String(opts.method || "GET").toUpperCase() === "GET";
  const cacheTTL = (opts && opts.cacheTTL) || DEFAULT_GET_TTL;
  const cacheKey = isGet ? url : null;
  if (isGet && cacheKey && !opts.force) {
    const cached = getCache.get(cacheKey);
    if (cached && cached.expires > Date.now()) {
      if (DEBUG >= 1) console.debug("[api] Returning cached GET for", cacheKey);
      // cleanup controller we created for this request
      if (controllerUsed) {
        activeControllers.delete(controllerUsed);
        if (requestTag) {
          const set = tagMap.get(requestTag);
          if (set) {
            set.delete(controllerUsed);
            if (set.size === 0) tagMap.delete(requestTag);
          }
        }
      }
      return cached.value;
    }
  }

  if (DEBUG >= 1) {
    try {
      console.debug("[api] Request", {
        method: opts.method || "GET",
        url,
        token: token ? String(token).slice(0, 12) + "..." : null,
        tokenSource,
      });
    } catch (e) {
      /* ignore debug errors */
    }
  }

  if (DEBUG >= 2) {
    try {
      const hdrs = {};
      for (const pair of headers.entries()) hdrs[pair[0]] = pair[1];
      console.debug("[api] Request Headers", hdrs);
      if (opts && opts.body && !(opts.body instanceof FormData)) {
        try {
          const bodyStr =
            typeof opts.body === "string"
              ? opts.body
              : JSON.stringify(opts.body);
          console.debug(
            "[api] Request Body",
            bodyStr.length > 2000
              ? bodyStr.slice(0, 2000) + "... (truncated)"
              : bodyStr,
          );
        } catch (e) {
          console.debug("[api] Request Body (unserializable)");
        }
      }
    } catch (e) {
      /* ignore debug errors */
    }
  }

  let res;
  try {
    res = await fetch(url, opts);
  } catch (e) {
    // fetch error (network or abort)
    if (controllerUsed) {
      activeControllers.delete(controllerUsed);
      if (requestTag) {
        const set = tagMap.get(requestTag);
        if (set) {
          set.delete(controllerUsed);
          if (set.size === 0) tagMap.delete(requestTag);
        }
      }
    }
    if (e && e.name === "AbortError") {
      const abortErr = new Error("Request aborted");
      abortErr.name = "AbortError";
      throw abortErr;
    }
    throw e;
  }

  // cleanup controller after response
  if (controllerUsed) {
    activeControllers.delete(controllerUsed);
    if (requestTag) {
      const set = tagMap.get(requestTag);
      if (set) {
        set.delete(controllerUsed);
        if (set.size === 0) tagMap.delete(requestTag);
      }
    }
  }

  if (DEBUG >= 1)
    console.debug("[api] Response", {
      status: res.status,
      statusText: res.statusText,
      url,
    });
  const contentType = res.headers.get("content-type") || "";
  if (res.ok) {
    if (contentType.includes("application/json")) {
      const json = await res.json();
      if (DEBUG >= 2) console.debug("[api] Response Body (json)", json);
      if (isGet && cacheKey) {
        getCache.set(cacheKey, { value: json, expires: Date.now() + cacheTTL });
      }
      return json;
    }
    const text = await res.text();
    if (DEBUG >= 2)
      console.debug("[api] Response Body (text)", text.slice(0, 2000));
    if (isGet && cacheKey) {
      getCache.set(cacheKey, { value: text, expires: Date.now() + cacheTTL });
    }
    return text;
  }

  if (DEBUG >= 1 && (res.status === 401 || res.status === 403)) {
    console.warn("[api] Unauthorized/forbidden", { status: res.status, url });
  }
  // Centralized handling: when token is invalid or missing, clear auth state so UI stops sending tokens.
  // Do NOT clear on 403; callers handle forbidden resources and the session may still be valid.
  if (res.status === 401) {
    try {
      store && store.dispatch && store.dispatch("auth/logout");
    } catch (e) {
      if (DEBUG) console.debug("[api] logout dispatch failed", e);
    }
    // optional redirect on auth failure to login
    try {
      if (localStorage.getItem("auto_logout_redirect") === "1") {
        window.location.href = "/login";
      }
    } catch (e) {
      /* ignore */
    }
  }
  const errorPayload = contentType.includes("application/json")
    ? await res.json().catch(() => null)
    : null;
  const err = new Error(res.statusText || "Request failed");
  err.status = res.status;
  err.payload = errorPayload;
  if (DEBUG >= 2) {
    try {
      console.error("[api] Error payload", err.status, err.payload);
    } catch (e) {
      console.debug("[api] debug-catch", e);
    }
  }
  throw err;
}

export const api = {
  get: (path, opts) => request(path, Object.assign({ method: "GET" }, opts)),
  options: (path, opts) =>
    request(path, Object.assign({ method: "OPTIONS" }, opts)),
  post: (path, body, opts) =>
    request(
      path,
      Object.assign(
        {
          method: "POST",
          body: body instanceof FormData ? body : JSON.stringify(body),
        },
        opts,
      ),
    ),
  put: (path, body, opts) =>
    request(
      path,
      Object.assign(
        {
          method: "PUT",
          body: body instanceof FormData ? body : JSON.stringify(body),
        },
        opts,
      ),
    ),
  del: (path, opts) => request(path, Object.assign({ method: "DELETE" }, opts)),
  getSchema: (path, opts) =>
    request(path, Object.assign({ method: "OPTIONS" }, opts)),
  getIds: async (path, opts) => {
    const idsPayload = await request(
      appendQuery(path, { id: "x" }),
      Object.assign({ method: "GET" }, opts),
    );
    return normalizeIds(idsPayload);
  },
  getFields: (path, id, fields = [], opts) =>
    request(
      appendQuery(path, {
        id,
        ...(fields.length
          ? Object.fromEntries(fields.map((f) => [f, "x"]))
          : {}),
      }),
      Object.assign({ method: "GET" }, opts),
    ),
  create: (path, body, opts) =>
    request(
      path,
      Object.assign(
        {
          method: "POST",
          body: body instanceof FormData ? body : JSON.stringify(body),
        },
        opts,
      ),
    ),
  update: (path, body, opts) =>
    request(
      path,
      Object.assign(
        {
          method: "PUT",
          body: body instanceof FormData ? body : JSON.stringify(body),
        },
        opts,
      ),
    ),
  remove: (path, idOrBody, opts = {}) => {
    const payload =
      typeof idOrBody === "number" || typeof idOrBody === "string"
        ? { id: idOrBody }
        : idOrBody;
    return request(
      path,
      Object.assign(
        { method: "DELETE" },
        payload ? { body: JSON.stringify(payload) } : {},
        opts,
      ),
    );
  },
  abortAll,
  abortTag,
};

export default api;
